import {merge} from '#core/utils/config-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {generatePivotButton} from '#core/application/modules/tiles/pivot/index.js';
import {generateSwitch} from '#core/application/modules/tiles/switch/index.js';
import {generatePrimaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {generateBetBaseInfo} from '#core/application/modules/tiles/bet-base-info/index.js';
import {generateToggleButton} from '#core/application/modules/tiles/button/index.js';
import {generateSecondaryLink} from '#core/application/modules/tiles/link/index.js';
import {generateStakeInput} from '#core/application/modules/tiles/stake-input/index.js';
import {CHECKBOX_TYPES, generateCheckbox} from '#core/application/modules/tiles/checkbox/index.js';
import {generateInfoNotification} from '#core/application/modules/tiles/notification/index.js';
import {generateOddsBoostBanner} from '#core/application/modules/tiles/odds-boost-banner/index.js';

export function betslipBetsFactory(PALETTE) {
    const {FONT} = PALETTE;
    const IS_MOBILE = isMobile();

    const BET_BASE_INFO = generateBetBaseInfo(PALETTE.BETSLIP_BET_BASE_INFO);
    const BETSLIP_BET_BONUS_PROMOTIONS_NOTIFICATION = generateInfoNotification(
        PALETTE.BETSLIP_BET_BONUS_PROMOTIONS_NOTIFICATION
    );
    const INPUT_FIX = {
        style: {
            base: {
                _mobile_maxWidth: 100,
            },
        },
    };

    return {
        BetBuilder: {
            removeAllButton: {
                style: {
                    base: {
                        ...resolveBackground(PALETTE.BETSLIP_REMOVE_ALL_BUTTON_CONTAINER_BG),
                        height: '40px',
                        width: 'fit-content',
                    },
                },
            },
            removeAllButtonText: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    ...PALETTE.BETSLIP_REMOVE_ALL_BUTTON.TERTIARY_BUTTON_TEXT,
                },
            },
            teaserOptions: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.BETSLIP_TEASERS_CONTAINER_BG),
                    },
                },
                dropdown: generatePrimaryDropdown(PALETTE.BETSLIP_TEASERS_DROPDOWN),
            },
            singleBetsList: {
                container: {
                    useSpacingSeparator: true, // set it explicitly
                    style: {
                        ...resolveBackground(PALETTE.BETSLIP_BETS_LIST_CONTAINER_BG),
                    },
                },
                separator: {
                    style: {
                        size: '1px',
                        margin: '0 8px',
                        color: PALETTE.BETSLIP_SEPARATOR_COLOR,
                    },
                },
                singleBet: {
                    betslipBet: {
                        container: {
                            padding: 8,
                            borderWidth: 0,
                            ...resolveBackground(PALETTE.BETSLIP_BET_CONTAINER_BG),
                        },
                    },
                    betBuilderContainer: {
                        ...resolveBackground(PALETTE.BETSLIP_BB_BET_CONTAINER_BG),
                        boxShadow: PALETTE.BETSLIP_BB_BET_CONTAINER_SHADOW,
                        borderRadius: PALETTE.BETSLIP_BB_BET_CONTAINER_BORDER_RADIUS,
                        margin: '8px 8px',
                    },
                    oddsBoostBanner: merge(generateOddsBoostBanner(PALETTE.BETSLIP_ODDS_BOOST_BANNER), {
                        betBuilderContainer: {
                            base: {
                                style: {
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: PALETTE.BETSLIP_BB_BET_CONTAINER_BORDER_RADIUS,
                                    borderBottomLeftRadius: PALETTE.BETSLIP_BB_BET_CONTAINER_BORDER_RADIUS,
                                    borderColor: PALETTE.BETSLIP_ODDS_BOOST_BANNER_SEPARATOR_COLOR,
                                    borderWidth: 0,
                                    borderTopWidth: '1px',
                                },
                            },
                            APPLIED: {
                                style: {
                                    // remove duplicated base styles after MOJ-15375
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: PALETTE.BETSLIP_BB_BET_CONTAINER_BORDER_RADIUS,
                                    borderBottomLeftRadius: PALETTE.BETSLIP_BB_BET_CONTAINER_BORDER_RADIUS,
                                    borderColor: PALETTE.BETSLIP_ODDS_BOOST_BANNER_SEPARATOR_COLOR,
                                    borderWidth: 0,
                                    borderTopWidth: '1px',
                                },
                            },
                        },
                    }),
                    betContent: {
                        default: {
                            marketSwitchDropdown: merge(generatePrimaryDropdown(PALETTE.BETSLIP_BET_MARKETS_DROPDOWN), {
                                style: {
                                    rootContainer: {
                                        padding: '0 25px 4px 0',
                                    },
                                },
                                controlText: {
                                    truncate: true,
                                },
                                menuItemText: {
                                    truncate: false,
                                },
                            }),
                            bankerButton: generateToggleButton(PALETTE.BETSLIP_BANKERS_BET_BUTTON),
                            // Moj use our style for selected spinner as unselected
                            bankerSpinner: {
                                image: icon(PALETTE.BETSLIP_BANKERS_BET_BUTTON.TOGGLE_BUTTON_SELECTED_ICON_COLOR),
                            },
                            bankerSpinnerSelected: {
                                image: icon(PALETTE.BETSLIP_BANKERS_BET_BUTTON.TOGGLE_BUTTON_ICON_COLOR),
                            },
                            multicastSelectionInfoContainer: {
                                style: {
                                    padding: 0,
                                },
                            },
                            multicastSelectionName: {
                                style: {
                                    ...BET_BASE_INFO.selectionName,
                                },
                            },
                            multicastMarketName: {
                                container: {
                                    padding: '4px 0',
                                },
                                style: {
                                    ...BET_BASE_INFO.marketName,
                                    fontWeight: FONT.WEIGHT_BOLD,
                                },
                            },
                            multicastEventName: {
                                style: {
                                    ...BET_BASE_INFO.eventName,
                                },
                            },
                            multicastAllOddsButton: generateSecondaryLink({
                                ...PALETTE.BETSLIP_BET_SHOW_ODDS_BUTTON,
                                SECONDARY_LINK_TEXT: {
                                    fontSize: FONT.SIZE_XXSMALL,
                                    textDecoration: 'underline',
                                    ...PALETTE.BETSLIP_BET_SHOW_ODDS_BUTTON.SECONDARY_LINK_TEXT,
                                },
                            }),
                            selectionName: {
                                style: {
                                    ...BET_BASE_INFO.selectionName,
                                },
                            },
                            selectionNameSuspended: {
                                style: {
                                    ...BET_BASE_INFO.selectionName,
                                },
                            },
                            marketName: {
                                style: {
                                    ...BET_BASE_INFO.marketName,
                                },
                            },
                            marketNameSuspended: {
                                style: {
                                    ...BET_BASE_INFO.marketName,
                                },
                            },
                            eventName: {
                                container: {
                                    padding: '0 10px 0 0',
                                },
                                style: {
                                    ...BET_BASE_INFO.eventName,
                                },
                            },
                            eventNameSuspended: {
                                style: {
                                    ...BET_BASE_INFO.eventName,
                                },
                            },
                            earlyPayoutIndicator: BET_BASE_INFO.earlyPayoutIndicator,
                        },
                        matchAcca: {
                            betslipBet: {
                                matchAccaImage: icon(PALETTE.BETSLIP_BB_BET_ICON_COLOR),
                                matchAccaHeaderContainer: {
                                    style: {
                                        alignItems: 'center',
                                    },
                                },
                            },
                            odds: {
                                suspendedInfo: {
                                    style: {
                                        fontWeight: FONT.WEIGHT_BOLD,
                                    },
                                },
                            },
                            matchAccaSelectionItem: merge(BET_BASE_INFO.betBuilderSelectionItem, {
                                selectionContainer: {
                                    style: {
                                        padding: '0 0 4px',
                                    },
                                },
                            }),
                        },
                    },
                },
            },
            systemBetsList: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.BETSLIP_BET_CONTAINER_BG),
                    },
                },
                labeledBet: {
                    totalOdds: BET_BASE_INFO.betTotalOdds,
                    betslipBet: {
                        container: {
                            padding: '8px',
                            ...resolveBackground(PALETTE.BETSLIP_BET_CONTAINER_BG),
                        },
                    },
                },
                betBonusPromotions: {
                    accaBoostNotification: BETSLIP_BET_BONUS_PROMOTIONS_NOTIFICATION,
                    accaInsuranceNotification: BETSLIP_BET_BONUS_PROMOTIONS_NOTIFICATION,
                    etbNotification: BETSLIP_BET_BONUS_PROMOTIONS_NOTIFICATION,
                },
            },
            betslipSettingsContainer: {
                style: {
                    ...resolveBackground(PALETTE.BETSLIP_SETTINGS_CONTAINER_BG),
                },
            },
            optionsContainer: {
                style: {
                    ...resolveBackground(PALETTE.BETSLIP_SETTINGS_CONTAINER_BG || 'inherit'),
                    ...(IS_MOBILE
                        ? {
                              borderStyle: 'solid',
                              borderColor: PALETTE.BETSLIP_SEPARATOR_COLOR,
                              borderWidth: '0 0 1px',
                          }
                        : {}),
                },
            },
            contentContainer: {
                useSpacingSeparator: true,
                style: {
                    // we need it because Moj didn't place removeAllButton inside container
                    ...resolveBackground(PALETTE.BETSLIP_REMOVE_ALL_BUTTON_CONTAINER_BG),
                },
                separator: {
                    // Separator between BS blocks (Remove all btn, single bets, labelled bets, placeBetContent)
                    style: {
                        size: 1,
                        color: PALETTE.BETSLIP_SEPARATOR_COLOR,
                    },
                },
            },
            emptyContentText: {
                style: {
                    ...PALETTE.BETSLIP_EMPTY_CONTENT_MESSAGE,
                },
            },
            tabs: {
                container: {
                    style: {
                        borderWidth: 0,
                        ...resolveBackground(PALETTE.BETSLIP_BET_TYPE_SELECTOR_CONTAINER_BG),
                    },
                },
                selectableButton: merge(generatePivotButton(PALETTE.BETSLIP_BET_TYPE_SELECTOR), {
                    selected: {
                        style: {
                            base: {
                                lineHeight: 1,
                                textAlign: 'center',
                            },
                        },
                    },
                    unselected: {
                        style: {
                            base: {
                                lineHeight: 1,
                                textAlign: 'center',
                            },
                        },
                    },
                }),
            },
            bankersToggleButton: merge(generateSwitch(PALETTE.BETSLIP_BANKERS_BETS_SWITCH), {
                mainContainer: {
                    style: {
                        justifyContent: 'flex-end',
                    },
                },
            }),
        },
        RemoveSelection: {
            removeSelectionButton: {
                innerContainer: {
                    style: {
                        padding: '13px 0 0 0', // align cross icon to text
                    },
                },
                button: {
                    style: {
                        base: {
                            alignItems: 'start',
                        },
                    },
                },
                buttonIcon: icon(PALETTE.BETSLIP_REMOVE_BET_BUTTON.CLOSE_BUTTON_ICON_COLOR, 'cross'),
            },
        },
        BetslipBet: {
            container: {
                fontSize: FONT.SIZE_MEDIUM,
                fontWeight: FONT.WEIGHT_NORMAL,
            },
            stakeInfoContainer: {
                style: {
                    padding: 0,
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_MEDIUM,
                    justifyContent: 'space-between',
                },
            },
            betInfoContainer: {
                style: {
                    padding: 0,
                    fontSize: 13,
                },
            },
            betType: BET_BASE_INFO.betType,
            betTypeSuspended: BET_BASE_INFO.betType,
            multiplesInfo: BET_BASE_INFO.multiplesInfo,
            multiplesInfoSuspended: BET_BASE_INFO.multiplesInfo,
            highlightIndicator: {
                style: {
                    ...resolveBackground(PALETTE.BETSLIP_BET_HIGHLIGHT_INDICATOR_COLOR),
                },
            },
            matchAccaHeader: {
                style: {
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.BETSLIP_BB_BET_TITLE_TEXT,
                },
            },
            matchAccaHeaderSuspended: {
                style: {
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_MEDIUM,
                    ...PALETTE.BETSLIP_BB_BET_TITLE_TEXT,
                },
            },
            matchAccaEventName: {
                container: {
                    padding: '4px 0 0',
                },
                style: {
                    ...BET_BASE_INFO.betBuilderMarketName,
                },
            },
            matchAccaEventNameSuspended: {
                container: {
                    padding: '4px 0 0',
                },
                style: {
                    ...BET_BASE_INFO.betBuilderMarketName,
                },
            },
        },
        Odds: {
            container: {
                style: {
                    justifyContent: 'flex-end',
                },
            },
            priceContainer: {
                style: {
                    ...BET_BASE_INFO.odds,
                    padding: '0 0 0 8px',
                },
            },
            priceBoostIndicator: BET_BASE_INFO.priceBoostValue,
            suspendedSelectionLabel: {
                suspendedText: {
                    container: {
                        ...BET_BASE_INFO.suspendedNotification,
                    },
                },
            },
            updatedPrice: {
                style: {
                    ...BET_BASE_INFO.oddsChangedValue.newValueContainer.style,
                    // these overrides have higher priority in this case
                    justifyContent: 'center',
                    padding: '4px',
                    maxHeight: '16px',
                },
            },
            bestOddsIcon: icon(PALETTE.BETSLIP_BEST_ODDS_GUARANTEED_ICON_COLOR),
        },
        OddsDropdown: {
            dropdown: merge(generatePrimaryDropdown(PALETTE.BETSLIP_ODDS_DROPDOWN), {
                controlButton: {
                    style: {
                        base: {
                            gap: '6px',
                            minWidth: '64px',
                            textAlign: 'center',
                        },
                    },
                },
            }),
            dropdownHighlight: merge(generatePrimaryDropdown(PALETTE.BETSLIP_ODDS_DROPDOWN), {
                controlButton: {
                    style: {
                        base: {
                            gap: '6px',
                            minWidth: '64px',
                            textAlign: 'center',
                            ...resolveBackground(PALETTE.BETSLIP_ODDS_DROPDOWN_HIGHLIGHT_BG),
                            ...PALETTE.BETSLIP_ODDS_DROPDOWN_HIGHLIGHT_LABEL,
                            fill: PALETTE.BETSLIP_ODDS_DROPDOWN_HIGHLIGHT_ROTATION_ICON,
                        },
                    },
                },
            }),
            suspendedSelectionLabel: {
                suspendedText: {
                    container: {
                        ...BET_BASE_INFO.suspendedNotification,
                    },
                },
            },
        },
        StakeInput: merge(generateStakeInput(PALETTE.BETSLIP_BET_STAKE_INPUT), {
            textInput: INPUT_FIX,
            customTextInput: INPUT_FIX,
            checkbox: merge(generateCheckbox(PALETTE.BETSLIP_BET_EACH_WAY_CHECKBOX, CHECKBOX_TYPES.VERTICAL), {
                container: {
                    style: {
                        base: {
                            margin: '0 12px 0 8px',
                        },
                    },
                },
            }),
        }),
        PotentialReturns: {
            style: {
                base: {
                    textStyle: {
                        fontSize: FONT.SIZE_XXSMALL,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.BETSLIP_BET_POTENTIAL_RETURNS_TEXT,
                    },
                },
                label: {
                    textStyle: {
                        fontSize: FONT.SIZE_XXSMALL,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.BETSLIP_BET_POTENTIAL_RETURNS_TEXT,
                    },
                },
                labelDisabled: {
                    textStyle: {
                        fontSize: FONT.SIZE_XXSMALL,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.BETSLIP_BET_POTENTIAL_RETURNS_TEXT,
                    },
                },
                disabled: {
                    textStyle: {
                        fontSize: FONT.SIZE_XXSMALL,
                        fontWeight: FONT.WEIGHT_NORMAL,
                        ...PALETTE.BETSLIP_BET_POTENTIAL_RETURNS_TEXT,
                    },
                },
            },
        },
        // used only for potential tax inside SingleBet and LabeledBet
        FormattedLabelWithPrice: {
            container: {
                style: {
                    padding: '4px 0px 0px',
                },
            },
            label: {
                style: {
                    fontSize: FONT.SIZE_XXSMALL,
                    ...PALETTE.BETSLIP_BET_POTENTIAL_TAX_TEXT,
                },
            },
            value: {
                style: {
                    fontSize: FONT.SIZE_XXSMALL,
                    ...PALETTE.BETSLIP_BET_POTENTIAL_TAX_TEXT,
                },
            },
        },
        // TODO: remove this global config and move it to BetBuilder.systemBetsList.*explicitNameForOddsBoostBanner* when Mojito deliver MOJ-15605
        OddsBoostBanner: generateOddsBoostBanner(PALETTE.BETSLIP_ODDS_BOOST_BANNER),
    };
}
